/**
 * Análises › Listas and Segurança › Acessos (D527; doc 20 section 7): the framework-neutral half of the pending reports' screens.
 *
 * The lists (`/v1/analytics/lists/:list`) and the access report (`/v1/ops/access/sessions`) answer pages of rows. This module holds what the screens
 * decide without drawing: which list needs which word (the menu and the controls follow the SERVER's rules, which re-check every call), the filter state
 * and the query it makes, the period choices (whole local days; the browser only says "30 days ending today" in UTC days - each market cuts its own day),
 * the choices a list offers from the rows it has already answered (drivers to keep, providers, campaigns), and the series a chart draws.
 */
import { type AccessQuery, type AdList, type AnalyticsListId, type AnalyticsListQuery, type DriverKmList, type QuoteList } from '@vello/api-client';
import { type ScreenPolicy } from './console-access.js';

export const LIST_TAB_ORDER: readonly AnalyticsListId[] = ['driver-km', 'customers', 'companies', 'requests', 'quotes', 'ads'];
export const DEFAULT_LIST: AnalyticsListId = 'driver-km';
export const LIST_PAGE = 50;
export const LIST_MAX_DAYS = 366;

/** Lists of people or of single transactions: the server asks `analytics:view_personal` over the whole scope for these. */
export const LIST_NEEDS_PERSONAL: ReadonlySet<AnalyticsListId> = new Set(['customers', 'requests']);

export const PERSONAL_POLICY: ScreenPolicy = { permission: 'analytics:view_personal', target: 'GEO' };
export const ACCESS_AUDIT_POLICY: ScreenPolicy = { permission: 'access:audit', target: 'COUNTRY' };

export const LIST_PRESETS = [7, 30, 90] as const;
export type ListPreset = (typeof LIST_PRESETS)[number] | 'custom';

export interface ListFilters {
  readonly list: AnalyticsListId;
  /** Market ids; empty = every market of the caller's reach. */
  readonly markets: readonly string[];
  readonly preset: ListPreset;
  readonly from: string;
  readonly to: string;
  /** driver-km: pseudonymous keys kept, the minimum km (text, as typed) and whether names and plates are asked. */
  readonly drivers: readonly string[];
  readonly minKm: string;
  readonly personal: boolean;
  readonly kinds: readonly string[];
  readonly minRequests: string;
  readonly providers: readonly string[];
  readonly categories: readonly string[];
  readonly campaigns: readonly string[];
  /** 0-based page. */
  readonly page: number;
}

export const INITIAL_LIST_FILTERS: ListFilters = {
  list: DEFAULT_LIST, markets: [], preset: 30, from: '', to: '', drivers: [], minKm: '', personal: false, kinds: [], minRequests: '', providers: [], categories: [], campaigns: [], page: 0,
};

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const dayMs = (day: string): number | undefined => {
  if (!DAY.test(day)) return undefined;
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const t = Date.UTC(y, m - 1, d);
  const probe = new Date(t);
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d ? t : undefined;
};
const dayOf = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

export type PeriodProblem = 'incomplete' | 'order' | 'too_long';

export function customPeriodProblem(from: string, to: string, maxDays: number = LIST_MAX_DAYS): PeriodProblem | undefined {
  const a = dayMs(from);
  const b = dayMs(to);
  if (a === undefined || b === undefined) return 'incomplete';
  if (b < a) return 'order';
  if (Math.round((b - a) / 86_400_000) + 1 > maxDays) return 'too_long';
  return undefined;
}

/** The two days a preset makes, ending `today` (a UTC day, the browser's clock); undefined while a custom pair is not valid. */
export function periodOf(preset: ListPreset, custom: { readonly from: string; readonly to: string }, today: string, maxDays: number = LIST_MAX_DAYS): { readonly from: string; readonly to: string } | undefined {
  if (preset === 'custom') return customPeriodProblem(custom.from, custom.to, maxDays) === undefined ? { from: custom.from, to: custom.to } : undefined;
  const end = dayMs(today);
  if (end === undefined) return undefined;
  return { from: dayOf(end - (preset - 1) * 86_400_000), to: today };
}

/**
 * The two days the period controls always show (owner, 2026-10-12: the dates are on screen beside the quick presets, no "custom"
 * step): a pressed preset's days ending `today`, or the pair typed so far. `'custom'` is only the marker for "dates typed, no
 * preset pressed" - no control offers it.
 */
export function shownDays(preset: ListPreset, custom: { readonly from: string; readonly to: string }, today: string): { readonly from: string; readonly to: string } {
  if (preset === 'custom') return { from: custom.from, to: custom.to };
  return periodOf(preset, custom, today) ?? { from: '', to: '' };
}

/** Editing one date pins the window to what is shown (the other edge keeps its day) and leaves every preset unpressed. */
export function editDays(preset: ListPreset, custom: { readonly from: string; readonly to: string }, today: string, edge: 'from' | 'to', day: string): { readonly preset: 'custom'; readonly from: string; readonly to: string } {
  const shown = shownDays(preset, custom, today);
  return { preset: 'custom', from: edge === 'from' ? day : shown.from, to: edge === 'to' ? day : shown.to };
}

export const toggled = <T extends string>(list: readonly T[], value: T, on: boolean): T[] => (on ? (list.includes(value) ? [...list] : [...list, value]) : list.filter((v) => v !== value));

const decimal = /^\d{1,6}(\.\d{1,3})?$/;

/** The query a filter state makes; undefined while the period is not valid. A filter the list ignores is not sent. */
export function listQuery(f: ListFilters, today: string, options: { readonly paging: boolean; readonly summaryOnly?: boolean } = { paging: true }): AnalyticsListQuery | undefined {
  const period = periodOf(f.preset, f, today);
  if (period === undefined) return undefined;
  const q: Record<string, unknown> = { from: period.from, to: period.to };
  if (f.markets.length > 0) q.market = f.markets;
  if (options.paging) {
    q.limit = LIST_PAGE;
    q.offset = f.page * LIST_PAGE;
  }
  if (f.list === 'driver-km' && options.summaryOnly === true) {
    // D527 review P1: per-driver figures are personal data - without `analytics:view_personal` only the per-market header is asked (no driver, no minimum, no names)
    q.part = 'summary';
  } else if (f.list === 'driver-km') {
    if (f.drivers.length > 0) q.driver = f.drivers;
    if (decimal.test(f.minKm.trim())) q.minKm = Number(f.minKm.trim());
    if (f.personal) q.personal = true;
  }
  if (f.list === 'customers' || f.list === 'requests') {
    if (f.kinds.length > 0) q.kind = f.kinds;
  }
  if (f.list === 'customers' && /^\d{1,6}$/.test(f.minRequests.trim()) && Number(f.minRequests.trim()) > 0) q.minRequests = Number(f.minRequests.trim());
  if (f.list === 'quotes') {
    if (f.providers.length > 0) q.provider = f.providers;
    if (f.categories.length > 0) q.category = f.categories;
  }
  if (f.list === 'ads' && f.campaigns.length > 0) q.campaign = f.campaigns;
  return q as AnalyticsListQuery;
}

/** A filter state with a list's own filters cleared (the tab changed): the period and the markets stay. */
export const switchList = (f: ListFilters, list: AnalyticsListId): ListFilters => ({ ...INITIAL_LIST_FILTERS, list, markets: f.markets, preset: f.preset, from: f.from, to: f.to });

export function hasListFilters(f: ListFilters): boolean {
  return f.drivers.length > 0 || f.minKm.trim() !== '' || f.kinds.length > 0 || f.minRequests.trim() !== '' || f.providers.length > 0 || f.categories.length > 0 || f.campaigns.length > 0;
}

export function listFileName(list: AnalyticsListId, period: { readonly from: string; readonly to: string }, part?: 'summary'): string {
  return `${list}${part === 'summary' ? '-summary' : ''}-${period.from}_${period.to}.csv`;
}

/** Kilometres from metres, three digits, for the municipal line (no rounding surprise: metres are exact). */
export const kmText = (meters: number, locale: string): string => new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(meters / 1000);

/** The drivers on a driver-km page, for the bar chart: the top by distance, labelled by name when the answer carries one, else by the short pseudonymous reference. */
export function driverBars(view: DriverKmList, max: number = 10): readonly { readonly key: string; readonly label: string; readonly value: number }[] {
  const totals = new Map<string, { label: string; meters: number }>();
  for (const r of view.rows) {
    const key = `${r.marketId}|${r.driverKey}`;
    const cur = totals.get(key);
    totals.set(key, { label: r.name ?? r.driverRef, meters: (cur?.meters ?? 0) + r.distanceMeters });
  }
  return [...totals.entries()].map(([key, v]) => ({ key, label: v.label, value: v.meters / 1000 })).sort((a, b) => b.value - a.value).slice(0, max);
}

/** The route providers a quote answer has seen (the choices to filter by), in order of first appearance, `UNKNOWN` last. */
export function providerChoices(view: QuoteList | undefined, chosen: readonly string[]): readonly string[] {
  const seen = new Set<string>(chosen);
  for (const r of view?.rows ?? []) seen.add(r.routeProvider);
  for (const m of view?.mix ?? []) seen.add(m.routeProvider);
  return [...seen].sort((a, b) => (a === 'UNKNOWN' ? 1 : b === 'UNKNOWN' ? -1 : a < b ? -1 : 1));
}

/** The provider mix as the series a stacked column chart draws: one series per provider, one value per day. */
export function mixSeries(view: QuoteList): { readonly days: readonly string[]; readonly series: readonly { readonly key: string; readonly values: readonly number[] }[] } {
  const days = [...new Set(view.mix.map((m) => m.day))].sort();
  const providers = providerChoices(view, []);
  return {
    days,
    series: providers.map((p) => ({ key: p, values: days.map((d) => view.mix.filter((m) => m.day === d && m.routeProvider === p).reduce((a, m) => a + m.quotes, 0)) })),
  };
}

/** The campaigns an ads answer has seen (the choices to keep). */
export function campaignChoices(view: AdList | undefined, chosen: readonly string[]): readonly string[] {
  const seen = new Set<string>(chosen);
  for (const r of view?.rows ?? []) seen.add(r.campaignId);
  return [...seen].sort();
}

/** Money spent per day in ONE currency (the first seen): a chart never shares an axis across currencies. */
export function adsSpendSeries(view: AdList): { readonly currency: string | null; readonly days: readonly string[]; readonly values: readonly number[] } {
  const currency = view.rows[0]?.currency ?? null;
  const perDay = new Map<string, bigint>();
  for (const r of view.rows) if (r.currency === currency && r.spentThatDay !== null) perDay.set(r.day, (perDay.get(r.day) ?? 0n) + BigInt(r.spentThatDay.amountMinor));
  const days = [...perDay.keys()].sort();
  return { currency, days, values: days.map((d) => Number(perDay.get(d))) };
}

// ───────────────────────────── the access report ─────────────────────────────

export const ACCESS_PAGE = 50;

export interface AccessFilters {
  readonly group: 'all' | 'operators' | 'clients';
  readonly preset: ListPreset;
  readonly from: string;
  readonly to: string;
  readonly user: string;
  readonly platforms: readonly ('ios' | 'android' | 'web')[];
  readonly status: '' | 'active' | 'revoked';
}

export const INITIAL_ACCESS_FILTERS: AccessFilters = { group: 'all', preset: 7, from: '', to: '', user: '', platforms: [], status: '' };
export const ACCESS_MAX_DAYS = 93;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const userFilterProblem = (user: string): boolean => user.trim() !== '' && !UUID.test(user.trim());

/** The query an access filter state makes (the cursor and the page size are the screen's); undefined while the period or the user is not valid. */
export function accessQuery(f: AccessFilters, today: string): AccessQuery | undefined {
  const period = periodOf(f.preset, f, today, ACCESS_MAX_DAYS);
  if (period === undefined || userFilterProblem(f.user)) return undefined;
  return {
    ...(f.group !== 'all' ? { group: f.group } : {}),
    from: period.from,
    to: period.to,
    ...(f.user.trim() !== '' ? { user: f.user.trim().toLowerCase() } : {}),
    ...(f.platforms.length > 0 ? { platform: f.platforms } : {}),
    ...(f.status !== '' ? { status: f.status } : {}),
  };
}

export function accessFileName(period: { readonly from: string; readonly to: string }): string {
  return `access-${period.from}_${period.to}.csv`;
}
